/**
 * 메뉴 카탈로그 (사이드바 · 역할세트 화면 공용)
 *
 * 정식 menu_key(35개: 운영 24 + 관리 6 + 전자결재 5)와 섹션 계층을 단일 소스로 정의한다.
 * - 사이드바: 섹션→항목 계층을 권한 필터링해 렌더(아이콘은 사이드바에서 별도 매핑).
 * - 역할세트 화면: 동일 카탈로그로 메뉴 체크박스 트리를 렌더.
 *
 * leaf id == app-sidebar/백엔드의 menu_key.
 */

export interface MenuCatalogItem {
  id: string;
  title: string;
  /** 관리 메뉴 표시용 메타(권한 판정에는 사용하지 않음) */
  isAdminOnly?: boolean;
  /**
   * 화면 안의 부가 동작 권한(수정·삭제 등). 서버 menu_catalog.parent_key 와 같은 계층이다.
   * 사이드바에는 렌더하지 않고(이동할 페이지가 없다) 역할세트 편집기에서 부모 항목 아래 "추가 권한"으로 보인다.
   * 계층은 1단계(화면 → 동작)로 고정한다.
   */
  actions?: Pick<MenuCatalogItem, 'id' | 'title'>[];
}

export interface MenuCatalogSection {
  sectionId: string;
  sectionTitle: string;
  /** 섹션 자체가 관리자 전용인지 여부 */
  isAdminOnly?: boolean;
  items: MenuCatalogItem[];
}

export const menuCatalog: MenuCatalogSection[] = [
  {
    sectionId: 'main',
    sectionTitle: '메인',
    items: [
      { id: 'dashboard', title: 'Dashboard' },
      { id: 'station-map', title: '주유소 지도' },
    ],
  },
  {
    sectionId: 'management',
    sectionTitle: '관리',
    items: [
      { id: 'user-approval', title: '가입 승인', isAdminOnly: true },
      { id: 'user-management', title: '사용자 관리', isAdminOnly: true },
      { id: 'organization-management', title: '조직 관리', isAdminOnly: true },
      { id: 'station-management', title: '주유소 정보 관리', isAdminOnly: true },
      { id: 'menu-role-sets', title: '역할세트 관리', isAdminOnly: true },
    ],
  },
  {
    sectionId: 'approval',
    sectionTitle: '전자결재',
    items: [
      { id: 'b210', title: '판매단가 승인요청' },
      { id: 'a330', title: '판매단가 승인' },
      { id: 'b220', title: '거래처 계약단가 승인요청' },
      { id: 'a340', title: '거래처 계약단가 승인' },
      { id: 'approval-admin', title: '전자결재 관리' },
    ],
  },
  {
    sectionId: 'aggregation',
    sectionTitle: '집계',
    items: [
      { id: 'salesinfo', title: '매출현황' },
      { id: 'salestotal', title: '판매구분별집계' },
      { id: 'cardsales', title: '신용카드 매출현황' },
      { id: 'selfsales', title: 'SELF 판매현황' },
      { id: 'timesale', title: '판매자료 분석' },
      { id: 'oct', title: 'OCT 현황' },
      { id: 'delivery', title: '배송현황' },
    ],
  },
  {
    sectionId: 'inout',
    sectionTitle: '입출금',
    items: [
      { id: 'money', title: '입출금자료' },
      { id: 'purchase', title: '매입자료' },
    ],
  },
  {
    sectionId: 'stock',
    sectionTitle: '재고',
    items: [
      { id: 'tankjg', title: '탱크재고' },
      { id: 'tlg-realtime', title: 'TLG 게이지 실시간' },
    ],
  },
  {
    sectionId: 'sales',
    sectionTitle: '판매',
    items: [
      { id: 'close-stat', title: '마감현황' },
      { id: 'aggregate', title: '종합일보' },
      { id: 'car-wash', title: '세차현황' },
    ],
  },
  {
    sectionId: 'finance',
    sectionTitle: '재무관리',
    items: [
      { id: 'invoice', title: '거래명세서' },
      { id: 'tax-invoice', title: '세금계산서' },
    ],
  },
  {
    sectionId: 'master',
    sectionTitle: '마스터',
    items: [
      { id: 'account', title: '계정 관리' },
      { id: 'car', title: '차량정보' },
      { id: 'cust-card', title: '거래카드정보' },
      { id: 'cust-cost', title: '고객별 계약 단가' },
      { id: 'nozzle', title: '노즐정보' },
      {
        id: 'cust',
        title: '거래처정보',
        actions: [{ id: 'cust-invoice-setting', title: '발행설정 수정' }],
      },
      { id: 'oil', title: '유종정보' },
      { id: 'tank', title: '탱크정보' },
    ],
  },
  {
    sectionId: 'system',
    sectionTitle: '시스템관리',
    isAdminOnly: true,
    items: [
      { id: 'data-transfer-status', title: '자료전송현황', isAdminOnly: true },
    ],
  },
];

/** 동작 권한 id → 소속 화면 id (서버 parent_key 와 동일). */
export const actionParentById: Record<string, string> = Object.fromEntries(
  menuCatalog.flatMap(section =>
    section.items.flatMap(item => (item.actions ?? []).map(action => [action.id, item.id] as const))
  )
);

export const isActionKey = (menuKey: string): boolean => menuKey in actionParentById;

/**
 * 화면 id → breadcrumb 항목(섹션 → 화면).
 *
 * 사이드바와 같은 카탈로그에서 만들어 메뉴 계층과 어긋나지 않게 한다 -
 * 화면마다 손으로 적던 시절에는 '매출/집계/…', '관리자용/…' 처럼 실제 메뉴에 없는
 * 경로가 섞여 있었다.
 */
export function menuBreadcrumbItems(
  menuId: string
): { label: string; isActive?: boolean }[] {
  const section = menuCatalog.find(s => s.items.some(item => item.id === menuId));
  const item = section?.items.find(item => item.id === menuId);
  if (!section || !item) return [];
  return [{ label: section.sectionTitle }, { label: item.title, isActive: true }];
}

/** 화면·동작 키 → 표시명. */
export const menuTitleById: Record<string, string> = Object.fromEntries(
  menuCatalog
    .flatMap(s => s.items)
    .flatMap(item => [[item.id, item.title], ...(item.actions ?? []).map(a => [a.id, a.title])])
);

/**
 * 역할세트 편집기의 키 토글. 화면과 동작 권한은 한 묶음으로 움직인다 —
 * 동작을 켜면 소속 화면도 켜고(조회 없는 쓰기 방지), 화면을 끄면 그 화면의 동작도 함께 끈다.
 * 서버 저장 가드(자식 키는 같은 세트에 부모 키 필수)와 같은 규칙이다.
 */
export function toggleMenuKey(selected: ReadonlySet<string>, id: string, checked: boolean): Set<string> {
  const next = new Set(selected);
  if (checked) {
    next.add(id);
    const parent = actionParentById[id];
    if (parent) next.add(parent);
    return next;
  }
  next.delete(id);
  for (const [action, parent] of Object.entries(actionParentById)) {
    if (parent === id) next.delete(action);
  }
  return next;
}
